import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { SubjectivityLibrarySearchRequestQuery, SubjectivityLibrarySearchResponse } from '@lio/api-types';
import { SubjectivityLibraryKeys } from '@services/subjectivity/subjectivity-library/subjectivityLibraryKeys';
import { buildQueryParamForArray } from '@utils/buildQueryParamForArray';

export type TSubjectivityLibraryItem = SubjectivityLibrarySearchResponse['items'][0];

const getQueryParams = (params: SubjectivityLibrarySearchRequestQuery) => {
  const { sort, searchText, createdByUser, pageLimit, pageOffset, insuranceLineId } = params;
  const query = new URLSearchParams();

  if (sort) {
    query.append('sort', sort);
  } else {
    query.append('sort', 'name');
  }

  if (searchText) {
    query.append('searchText', String(searchText));
  }

  if (createdByUser) {
    query.append('createdByUser', String(createdByUser));
  }

  if (pageLimit) {
    query.append('pageLimit', String(pageLimit));
  }

  if (pageOffset) {
    query.append('pageOffset', String(pageOffset));
  }

  if (insuranceLineId) {
    buildQueryParamForArray(query, 'insuranceLineId', insuranceLineId);
  }

  return query.toString();
};

export const useSearchPaginatedSubjectivityLibrary = ({
  params = { pageLimit: 10 },
  enabled,
}: {
  params?: SubjectivityLibrarySearchRequestQuery;
  enabled?: boolean;
  pagination?: boolean;
}) => {
  const client = useClient();
  const query = getQueryParams(params);

  const requestUrl = `subjectivity/library-item?${query}`;

  const result = useInfiniteQuery({
    queryKey: ['infinite', SubjectivityLibraryKeys.searchParams({ ...params })],
    queryFn: async () => client.get(`${requestUrl}`).json<SubjectivityLibrarySearchResponse>(),
    enabled: enabled,
    initialPageParam: 0,
    staleTime: 0,
    gcTime: 0,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    retry: false,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.pagination) return undefined;
      if (!lastPage.pagination.pageLimit) return undefined;
      if (!lastPage.pagination.total) return undefined;
      const { total, pageLimit } = lastPage.pagination;
      const fetchedPageCount = allPages.length;
      const nextPageOffsetValue = fetchedPageCount * pageLimit;
      if (nextPageOffsetValue > total) return undefined;
      return nextPageOffsetValue;
    },
  });

  return { ...result, items: result.data?.pages.flatMap((page) => page.items) as TSubjectivityLibraryItem[] };
};

export const useSearchSubjectivityLibrary = (params: SubjectivityLibrarySearchRequestQuery, enabled = false) => {
  const client = useClient();

  const query = getQueryParams({ ...params });

  const result = useQuery({
    enabled: enabled,
    queryKey: SubjectivityLibraryKeys.searchParams({ ...params }),
    queryFn: () => client.get(`subjectivity/library-item?${query}`).json<SubjectivityLibrarySearchResponse>(),
    refetchOnWindowFocus: false,
  });
  return {
    ...result,
    items: result.data?.items as SubjectivityLibrarySearchResponse['items'],
  };
};
